import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ExternalLink, Gavel } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatDate, cn } from '@/lib/utils';

// Field shape mirrors the PracAction Prisma model and GET /api/prac/:id response.
interface PracActionDetail {
  id: string;
  tenantId: string;
  signalId: string | null;
  recommendationType: string;
  sourceUrl: string | null;
  issuedAt: string;
  dueDate: string | null;
  status: 'open' | 'in_progress' | 'completed' | 'cancelled' | string;
  linkedVariationRef: string | null;
  linkedPsurId: string | null;
  linkedRmpId: string | null;
  completedAt: string | null;
  completedBy: string | null;
  evidence: Record<string, unknown> | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface AuditEntry {
  id: string;
  action: string;
  description: string | null;
  createdAt: string;
  userId: string | null;
  previousState?: unknown;
  newState?: unknown;
}

type TabKey = 'summary' | 'links' | 'evidence' | 'audit';

export function PracActionDetailPage() {
  const { id } = useParams();
  const [tab, setTab] = useState<TabKey>('summary');

  const action = useQuery({
    queryKey: ['prac', id],
    queryFn: async () => {
      try {
        return (await api.get(`/api/prac/${id}`)).data as PracActionDetail;
      } catch {
        return null;
      }
    },
  });

  // Best-effort audit trail. Endpoint shape per apps/api/src/modules/audit;
  // if the request fails we silently fall back to an empty list.
  const audit = useQuery({
    queryKey: ['prac', id, 'audit'],
    queryFn: async () => {
      try {
        const params = new URLSearchParams();
        params.set('resource', 'prac_action');
        params.set('resourceId', id ?? '');
        params.set('pageSize', '50');
        const r = await api.get(`/api/audit?${params.toString()}`);
        return (r.data?.items ?? []) as AuditEntry[];
      } catch {
        return [] as AuditEntry[];
      }
    },
    enabled: !!id,
  });

  if (action.isLoading) return <div className="text-sm text-muted-foreground">Loading...</div>;
  if (!action.data) return <NotFound />;

  const a = action.data;

  return (
    <div className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2">
          <Link to="/prac">
            <ArrowLeft className="mr-2 h-4 w-4" /> PRAC actions
          </Link>
        </Button>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
              <Gavel className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">
                {a.recommendationType.replace(/_/g, ' ')}
              </h1>
              <p className="text-sm text-muted-foreground">
                Reference <span className="font-mono">{a.id.slice(0, 8)}</span> · issued{' '}
                {formatDate(a.issuedAt)}
                {a.dueDate ? ` · due ${formatDate(a.dueDate)}` : ''}
              </p>
            </div>
          </div>
          <StatusBadge s={a.status} />
        </div>
      </div>

      <div className="flex gap-1 border-b">
        {(
          [
            ['summary', 'Summary'],
            ['links', 'Linked workflows'],
            ['evidence', 'Evidence'],
            ['audit', 'Audit trail'],
          ] as [TabKey, string][]
        ).map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={cn(
              '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              tab === k
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {l}
          </button>
        ))}
      </div>

      {tab === 'summary' && (
        <Card>
          <CardHeader>
            <CardTitle>Summary</CardTitle>
            <CardDescription>
              Public PRAC recommendation as captured from the source meeting / monthly minutes.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row label="Recommendation type" value={a.recommendationType.replace(/_/g, ' ')} />
            <Row label="Status" value={a.status.replace(/_/g, ' ')} />
            <Row label="Issued" value={formatDate(a.issuedAt)} />
            <Row label="Due" value={a.dueDate ? formatDate(a.dueDate) : '-'} />
            <Row
              label="Source"
              value={
                a.sourceUrl ? (
                  <a
                    href={a.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    Open <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  '-'
                )
              }
            />
            <Row
              label="Linked signal"
              value={
                a.signalId ? (
                  <Link to={`/signals/${a.signalId}`} className="text-primary hover:underline">
                    {a.signalId}
                  </Link>
                ) : (
                  '-'
                )
              }
            />
            <Row
              label="Notes"
              value={a.notes ? <span className="whitespace-pre-wrap">{a.notes}</span> : '-'}
            />
            {a.completedAt && (
              <>
                <Row label="Completed" value={formatDate(a.completedAt)} />
                <Row label="Completed by" value={a.completedBy ?? '-'} />
              </>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 'links' && (
        <Card>
          <CardHeader>
            <CardTitle>Downstream workflows</CardTitle>
            <CardDescription>
              How this PRAC recommendation was actioned (variation, PSUR, RMP).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row label="Linked variation" value={a.linkedVariationRef ?? '-'} />
            <Row
              label="Linked PSUR"
              value={
                a.linkedPsurId ? (
                  <Link to={`/psur/${a.linkedPsurId}`} className="text-primary hover:underline">
                    {a.linkedPsurId}
                  </Link>
                ) : (
                  '-'
                )
              }
            />
            <Row
              label="Linked RMP"
              value={
                a.linkedRmpId ? (
                  <Link to={`/rmp/${a.linkedRmpId}`} className="text-primary hover:underline">
                    {a.linkedRmpId}
                  </Link>
                ) : (
                  '-'
                )
              }
            />
          </CardContent>
        </Card>
      )}

      {tab === 'evidence' && (
        <Card>
          <CardHeader>
            <CardTitle>Closure evidence</CardTitle>
            <CardDescription>
              Stored under the PRAC action when the recommendation is closed.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {a.evidence && Object.keys(a.evidence).length > 0 ? (
              <pre className="max-h-96 overflow-auto rounded-md bg-muted p-3 text-xs">
                {JSON.stringify(a.evidence, null, 2)}
              </pre>
            ) : (
              <p className="text-sm text-muted-foreground">No evidence recorded yet.</p>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 'audit' && (
        <Card>
          <CardHeader>
            <CardTitle>Audit trail</CardTitle>
            <CardDescription>
              Every state change on this PRAC action, written to the immutable audit log.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {audit.isLoading && (
              <p className="text-sm text-muted-foreground">Loading audit trail...</p>
            )}
            {!audit.isLoading && (audit.data?.length ?? 0) === 0 && (
              <p className="text-sm text-muted-foreground">No audit entries.</p>
            )}
            {audit.data?.map((e) => (
              <div key={e.id} className="rounded-md border p-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{e.action.replace(/_/g, ' ')}</span>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(e.createdAt)}
                  </span>
                </div>
                {e.description && (
                  <div className="mt-1 text-xs text-muted-foreground">{e.description}</div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b py-2 last:border-b-0">
      <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}

function StatusBadge({ s }: { s: string }) {
  const v: Record<string, 'secondary' | 'info' | 'success' | 'destructive' | 'warning'> = {
    open: 'info',
    in_progress: 'warning',
    completed: 'success',
    cancelled: 'secondary',
  };
  return <Badge variant={v[s] ?? 'secondary'}>{s.replace(/_/g, ' ')}</Badge>;
}

function NotFound() {
  return (
    <div className="space-y-4">
      <Button asChild variant="ghost" size="sm" className="-ml-2">
        <Link to="/prac">
          <ArrowLeft className="mr-2 h-4 w-4" /> PRAC actions
        </Link>
      </Button>
      <Card>
        <CardContent className="p-6 text-sm text-muted-foreground">
          PRAC action not found, or you don't have access to it in this tenant.
        </CardContent>
      </Card>
    </div>
  );
}
